import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, ChevronDown, ChevronRight, ArrowLeft, Search, BookOpen } from 'lucide-react';

const sections = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    steps: [
      { title: 'Create your account', body: 'Go to the registration page and sign up with your email and password, or use Google Sign-In for faster access. You\'ll be asked for your name and organization details.' },
      { title: 'Log in to the dashboard', body: 'After registration, log in at /login. You\'ll land on your Dashboard which shows your profiles, artifacts, and quick stats.' },
      { title: 'Explore the sidebar', body: 'The left sidebar gives you access to all sections: Profiles, Artifacts, Scheduling, Customers, Leads, Analytics, Shop, Orders, and Settings.' },
    ],
  },
  {
    id: 'profiles',
    title: 'Managing Profiles',
    steps: [
      { title: 'View your profiles', body: 'From the Dashboard, you\'ll see your profile cards. Each profile has a color code (Green, Blue, Red, Yellow, Purple, Black) and can be customized independently.' },
      { title: 'Edit a profile', body: 'Click on any profile card to open the Profile Editor. Here you can update your name, title, bio, phone, email, and website.' },
      { title: 'Upload a profile photo', body: 'In the Profile Editor under "Profile Photo", click "Upload" to select a JPEG, PNG, or WebP image (max 5MB). You can change or remove it anytime.' },
      { title: 'Upload a logo', body: 'Under "Logo", upload your brand or company logo. Supports JPEG, PNG, WebP, and SVG formats (max 5MB). The logo appears on your public profile header.' },
      { title: 'Add services', body: 'Scroll to the Services section. Click "Add Service" and fill in the service name, description, and price. Services are displayed on your public profile.' },
      { title: 'Upload videos', body: 'In the Media section, click "Upload Video" to add video content (MP4, WebM, MOV — max 100MB). Videos play directly on your public profile.' },
      { title: 'Upload photos', body: 'Click "Upload Photo" to add gallery images (JPEG, PNG, WebP — max 10MB). Photos appear in a gallery section on your profile.' },
      { title: 'Upload PDFs', body: 'Add downloadable documents like brochures or catalogs. Visitors can access these from your profile, with optional lead gating (they provide contact info before downloading).' },
      { title: 'Set contact mode', body: 'In the Visibility tab, choose how people interact with your profile:\n• Full — all contact info visible\n• Lead Form Only — visitors must submit a form to see contact info\n• Broadcast — hides everything except videos and a call-to-action button (ideal for campaigns)' },
      { title: 'Toggle section visibility', body: 'In the Visibility tab, toggle individual sections on/off: Header, Contact, Services, Photos, Videos, PDFs, Lead Form, and CTA. At least one content section must remain visible.' },
      { title: 'Preview your public profile', body: 'Click the "View Public Profile" link to see exactly what visitors see when they scan your artifact.' },
    ],
  },
  {
    id: 'broadcast',
    title: 'Broadcast Mode',
    steps: [
      { title: 'What is Broadcast Mode?', body: 'Broadcast mode turns your profile into a minimal campaign landing page. It hides all personal information (name, photo, contact) and shows only your video content and a call-to-action button.' },
      { title: 'Enable Broadcast Mode', body: 'In the Profile Editor\'s Visibility tab, select "Broadcast" as your contact mode. This automatically sets: Header off, Contact off, Services off, Photos off, Videos on, PDFs off, CTA on.' },
      { title: 'Customize after enabling', body: 'After switching to Broadcast, you can still manually toggle sections. For example, enable the Lead Form to collect contacts while in broadcast mode.' },
      { title: 'Use case', body: 'Perfect for trade shows, events, or ad campaigns where you want visitors to watch a video and take action — without revealing personal details.' },
    ],
  },
  {
    id: 'artifacts',
    title: 'Artifacts (NFC/QR)',
    steps: [
      { title: 'What are artifacts?', body: 'Artifacts are physical NFC cards, QR cubes, smart rings, bracelets, pendants, or keychains. Each artifact has a unique URL that, when scanned, redirects to your assigned profile.' },
      { title: 'View your artifacts', body: 'Go to "Artifacts" in the sidebar. You\'ll see all artifacts assigned to you by your admin. Each shows its type and assigned profile.' },
      { title: 'Assign a profile to an artifact', body: 'For each artifact, use the profile dropdown to assign which profile visitors will see when they scan it. You can change this anytime.' },
      { title: 'Bulk assign to a campaign', body: 'Select multiple artifacts using the checkboxes, then click "Assign to Campaign". Choose an existing campaign or create a new one. Campaign artifacts switch to "giveaway" mode for tracking.' },
      { title: 'View campaign analytics', body: 'In the Campaigns tab, click on a campaign to expand it. You\'ll see stats: taps, unique visitors, leads collected, video plays, and PDF downloads.' },
      { title: 'QR Cube configuration', body: 'Go to the "QR Cube" tab to configure which profile each color face of the cube points to. You can download QR codes for each face individually, or get a full print sheet.' },
    ],
  },
  {
    id: 'scheduling',
    title: 'Profile Scheduling',
    steps: [
      { title: 'What is scheduling?', body: 'Scheduling lets you automatically switch which profile is active at different times. For example, show your "Sales" profile during business hours and your "Personal" profile in the evening.' },
      { title: 'Create a schedule', body: 'Go to "Scheduling" in the sidebar. Click "Add Schedule" and set the profile, days of the week, start time, and end time.' },
      { title: 'How it works', body: 'When someone scans your artifact during a scheduled time, they\'ll see the scheduled profile instead of the default one. Outside scheduled times, the default profile is shown.' },
    ],
  },
  {
    id: 'customers',
    title: 'Customers & Leads',
    steps: [
      { title: 'View leads', body: 'Go to "Leads" in the sidebar. Every time someone fills out a lead form on your profile, their contact info appears here with the date and source profile.' },
      { title: 'Manage customers', body: 'Go to "Customers" to manage your contact database. You can add customers manually or they\'re created automatically from leads.' },
      { title: 'Assign customers to giveaway artifacts', body: 'When using artifacts in campaign/giveaway mode, you can assign a specific customer to each artifact to track who received which artifact.' },
    ],
  },
  {
    id: 'shop',
    title: 'Shop & Orders',
    steps: [
      { title: 'Browse the shop', body: 'Visit the Shop from the sidebar or at /shop. Browse available NFC cards, QR cubes, and other artifacts. Add items to your cart.' },
      { title: 'Checkout', body: 'Review your cart and proceed to checkout. Fill in your name, email, and shipping address to place an order.' },
      { title: 'Track orders', body: 'Go to "Orders" in the sidebar to view your order history, status, and details.' },
    ],
  },
  {
    id: 'analytics',
    title: 'Analytics',
    steps: [
      { title: 'View your analytics', body: 'Go to "Analytics" in the sidebar. See overall stats across all your profiles: total taps, unique visitors, leads collected, and more.' },
      { title: 'Profile-level analytics', body: 'Each profile tracks views, lead submissions, video plays, and PDF downloads. Use this to understand which profiles and content resonate most.' },
      { title: 'Campaign analytics', body: 'Campaign artifacts track engagement metrics separately, making it easy to measure ROI from events and promotions.' },
    ],
  },
  {
    id: 'admin',
    title: 'Admin Guide (Super Admin Only)',
    steps: [
      { title: 'Access the admin panel', body: 'Super Admins see an "Admin" section in the sidebar. Click it to access the admin dashboard with platform-wide stats.' },
      { title: 'Create artifacts', body: 'Go to Admin > Artifacts. Click "Bulk Create" to generate multiple artifacts at once. Choose the type, quantity (1–100), and an optional label prefix.' },
      { title: 'Assign artifacts to users', body: 'In the artifacts table, find unassigned artifacts and use the "Assign user" dropdown to assign them. Once assigned, artifacts cannot be reassigned.' },
      { title: 'View artifact URLs', body: 'The admin table shows the tap URL and NFC token for each artifact. Click the copy button to copy URLs, or the external link to test them. These URLs are hidden from regular users.' },
      { title: 'Manage the shop catalog', body: 'Go to Admin > Catalog. Add, edit, or remove products. Upload product images, set prices, toggle visibility (active/hidden), and drag to reorder how items appear on the shop page.' },
      { title: 'Manage orders', body: 'Go to Admin > Orders to view all customer orders across the platform. Update order status (pending, confirmed, shipped, delivered, cancelled).' },
    ],
  },
];

const ManualPage = () => {
  const [openSection, setOpenSection] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSections = searchQuery
    ? sections.map(s => ({
        ...s,
        steps: s.steps.filter(step =>
          step.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          step.body.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      })).filter(s => s.steps.length > 0)
    : sections;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors" data-testid="back-link">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Signa</span>
              <span className="text-slate-400 mx-1">|</span>
              <span className="text-slate-600 font-medium flex items-center gap-1.5"><BookOpen className="w-4 h-4" /> User Manual</span>
            </div>
          </div>
          <Link to="/login" className="text-sm font-medium text-indigo-600 hover:text-indigo-700" data-testid="login-link">Log in</Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8 flex gap-8">
        {/* Table of Contents (sidebar) */}
        <nav className="hidden lg:block w-56 flex-shrink-0 sticky top-24 self-start">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Contents</p>
          <ul className="space-y-1">
            {sections.map(s => (
              <li key={s.id}>
                <button
                  onClick={() => { setOpenSection(s.id); setSearchQuery(''); document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                  className={`block w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${openSection === s.id ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-slate-600 hover:bg-slate-100'}`}
                  data-testid={`toc-${s.id}`}
                >
                  {s.title}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          {/* Search */}
          <div className="relative mb-8">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search the manual..."
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              data-testid="manual-search"
            />
          </div>

          {/* Sections */}
          <div className="space-y-4">
            {filteredSections.map(section => (
              <div key={section.id} id={section.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`section-${section.id}`}>
                <button
                  onClick={() => setOpenSection(openSection === section.id ? null : section.id)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{section.title}</h2>
                  {openSection === section.id ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                </button>
                {openSection === section.id && (
                  <div className="px-6 pb-6 border-t border-slate-100">
                    <ol className="space-y-5 mt-5">
                      {section.steps.map((step, i) => (
                        <li key={i} className="flex gap-4" data-testid={`step-${section.id}-${i}`}>
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-bold mt-0.5">
                            {i + 1}
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-1">{step.title}</h3>
                            <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{step.body}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            ))}
          </div>

          {searchQuery && filteredSections.length === 0 && (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No results for "{searchQuery}"</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ManualPage;
